import { yupResolver } from "@hookform/resolvers/yup";
import { Fragment, useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import * as yup from "yup";

import { CourseDocumentApi, DocumentTemplateApi } from "api";
import { useDocumentTemplatesOptions } from "api/hooks";
import { ClipboardText } from "components/atoms";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { DocumentPreview } from "pages/Documents/DocumentPreview";
import { DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES } from "pages/Documents/PagePlaceholdersForm";
import { useCourseDocumentExtraPages } from "../../../api/hooks/useCourseDocumentExtraPages";
import { PaziaFormButton, PaziaInput, PaziaLoader, PaziaSelect, PaziaTooltip } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { ExtraPageUpload } from "./components/ExtraPageUpload";
import { ExtraPagesList } from "./components/ExtraPagesList";

interface CourseDocumentFormProps {
  onSubmit: (data: DocumentFormValues) => Promise<void>;
  courseId: number;
  document?: CourseDocumentApi;
}

export const CourseDocumentForm = ({ onSubmit, courseId, document }: CourseDocumentFormProps): JSX.Element => {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      [FORM_NAMES.NAME]: document?.name ?? "",
      [FORM_NAMES.TEMPLATEID]: document?.template?.id?.toString() ?? "",
      [FORM_NAMES.ACTIVE_PAGE]: "basic",
      ...document?.placeholders.reduce(
        (obj, item) => {
          obj[item.documentTemplatePlaceholderId.toString()] = item.placeholderValue;
          return obj;
        },
        {} as Record<string, string>,
      ),
    },
  });
  const { handleSubmit, watch, setValue } = methods;
  const { isLoading: loadingTemplates, options: templateOptions } = useDocumentTemplatesOptions();
  const activePage = watch("activePage");

  // Get template after hooks but before conditional returns
  const templateId = watch(FORM_NAMES.TEMPLATEID);
  const template = templateOptions.find((x) => x.value == templateId)?.template;

  // Set active page to first page when template changes (but keep basic as default)
  useEffect(() => {
    // Only auto-switch to first template page if we're not already on the basic tab
    const currentActivePage = watch(FORM_NAMES.ACTIVE_PAGE);
    if (template && template.pages.length > 0 && currentActivePage !== "basic") {
      setValue(FORM_NAMES.ACTIVE_PAGE, template.pages[0].pageId);
    }
  }, [template, setValue, watch]);

  if (loadingTemplates) return <PaziaLoader position="center" />;

  return (
    <FormProvider {...methods}>
      <PlaceholdersWarningPanel type={template?.type} />
      <PageTabs template={template} document={document} />
      <div className="grid grid-cols-12 gap-8">
        {/* Left Side - Form Content */}
        <div className="col-span-4 space-y-6">
          {/* Main Form Section */}
          <div className="">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Show tabs always */}

              <div className="py-6">
                <BasicInfoTab />
                {template && <PlaceholdersTab template={template} />}
                <ExtraPagesTab courseId={courseId} document={document} />
              </div>

              <div className="flex justify-end border-t border-slate-200 pt-6 dark:border-slate-700">
                {activePage !== "basic" && <PaziaFormButton title="Zapisz zmiany" type="success" />}
              </div>
            </form>
          </div>
        </div>

        {/* Right Side - Preview */}
        <div className="col-span-8">
          <DocumentEditPreview document={template} />
        </div>
      </div>
    </FormProvider>
  );
};

const DocumentEditPreview = ({ document }: { document?: DocumentTemplateApi | null }) => {
  const { watch } = useFormContext();
  const activePage = watch("activePage");
  if (!document)
    return (
      <div className="sticky top-8">
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
            <svg className="h-6 w-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">Wybierz szablon</h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Podgląd pojawi się po wybraniu szablonu dokumentu
          </p>
        </div>
      </div>
    );

  return (
    <div className="sticky top-8">
      <div className="">
        <div className="border-b border-slate-200 p-4 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Podgląd dokumentu</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Interaktywny podgląd z danymi wprowadzonymi w formularzu
          </p>
        </div>
        <div className="p-4">
          {document.pages.map((page) => (
            <DocumentPreview
              key={page.pageId}
              className={page.pageId == activePage ? "" : "!hidden"}
              hide={page.pageId != activePage}
              backgroundImage={page.background}
              docHeight={document.height}
              fillPlaceholdersBackground
              docWidth={document.width}
              placeholders={page.placeholders.map((x) => {
                const example = Object.entries(watch()).find((o) => o[0] == x.id.toString());
                return {
                  ...x,
                  width: Number(x.width), // TODO What de hek
                  height: Number(x.height),
                  left: Number(x.left),
                  top: Number(x.top),
                  type: x.type.toString(),
                  fontSize: Number(x.fontSize),
                  example: example ? example[1] : x.example,
                };
              })}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const PageTabs = ({ template, document }: { template?: DocumentTemplateApi | null; document?: CourseDocumentApi }) => {
  const { watch, setValue } = useFormContext();
  const activePage = watch("activePage");

  const handleTabClick = (pageId: number | string) => {
    setValue("activePage", pageId);
  };

  return (
    <div className="border-b border-slate-200 dark:border-slate-700">
      <nav className="-mb-px flex space-x-8">
        {/* Podstawowe informacje tab */}
        <button
          type="button"
          onClick={() => handleTabClick("basic")}
          className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium transition-colors duration-200 ${
            activePage === "basic"
              ? "border-primary-500 text-primary-600 dark:text-primary-400"
              : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-300"
          }`}
        >
          Podstawowe informacje
        </button>

        {/* Template page tabs */}
        {template?.pages.map((page) => (
          <button
            key={page.pageId}
            type="button"
            onClick={() => handleTabClick(page.pageId)}
            className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium transition-colors duration-200 ${
              activePage === page.pageId
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-300"
            }`}
          >
            Strona {page.pageNumber}
          </button>
        ))}

        {/* Extra Pages tab - only show for existing documents */}
        {document?.id && (
          <button
            type="button"
            onClick={() => handleTabClick("extraPages")}
            className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium transition-colors duration-200 ${
              activePage === "extraPages"
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-300"
            }`}
          >
            Dodatkowe strony
          </button>
        )}
      </nav>
    </div>
  );
};

const BasicInfoTab = () => {
  const { watch } = useFormContext();
  const activePage = watch(FORM_NAMES.ACTIVE_PAGE);
  const { options: templateOptions } = useDocumentTemplatesOptions();

  if (activePage !== "basic") return null;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Podstawowe informacje</h2>
      <div className="grid gap-4">
        <PaziaInput name={FORM_NAMES.NAME} label="Nazwa dokumentu" />
        <PaziaSelect
          label="Wybierz szablon"
          name={FORM_NAMES.TEMPLATEID}
          options={templateOptions}
          isClearable={false}
        />
      </div>
    </div>
  );
};

const PlaceholdersTab = ({ template }: { template: DocumentTemplateApi }) => {
  const { watch } = useFormContext();
  const activePage = watch(FORM_NAMES.ACTIVE_PAGE);

  if (activePage === "basic" || activePage === "extraPages") return null;
  return (
    <div className="space-y-4">
      <Placeholders template={template} />
    </div>
  );
};

const ExtraPagesTab = ({ courseId, document }: { courseId: number; document?: CourseDocumentApi }) => {
  const { watch } = useFormContext();
  const activePage = watch(FORM_NAMES.ACTIVE_PAGE);
  const { data: extraPages } = useCourseDocumentExtraPages(courseId, document?.id ?? 0);
  const nextOrderNumber = extraPages.length > 0 ? Math.max(...extraPages.map((p) => p.order)) + 1 : 1;

  if (activePage !== "extraPages") return null;

  if (!document?.id) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Dodatkowe strony</h2>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Dodatkowe strony będą dostępne po zapisaniu dokumentu.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Dodatkowe strony</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Dodaj pliki PDF lub DOCX, które będą dołączone do generowanych dokumentów. Pliki zostaną dodane w określonej
          kolejności po głównym dokumencie.
        </p>
      </div>

      <div className="space-y-4 border-t border-slate-200 pt-4 dark:border-slate-700">
        <ExtraPageUpload courseId={courseId} courseDocumentId={document.id} nextOrderNumber={nextOrderNumber} />
        <ExtraPagesList courseId={courseId} courseDocumentId={document.id} />
      </div>
    </div>
  );
};

interface PlaceholderProps {
  template: DocumentTemplateApi;
}

const Placeholders = ({ template }: PlaceholderProps) => {
  const { unregister, watch, setValue } = useFormContext();
  const activePage = watch("activePage");

  useEffect(() => {
    template.pages.forEach((page) => {
      page.placeholders.forEach((placeholder) => {
        if (placeholder.type == DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES.IMAGE) {
          setValue(placeholder.id.toString(), "{{student.zdjecieDokument}}");
        }
      });
    });
  }, [template, setValue]);

  useEffect(() => {
    return () => {
      template.pages.forEach((page) => {
        page.placeholders.forEach((placeholder) => {
          unregister(placeholder.id.toString());
        });
      });
    };
  }, [template]);

  return (
    <Fragment>
      {template.pages.map((page) => {
        if (page.placeholders.length === 0)
          return (
            <div className={`${activePage === page.pageId ? "" : "hidden"}`} key={page.pageId}>
              <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                Brak pól do wypełnienia na tej stronie
              </p>
            </div>
          );
        return (
          <div className={`space-y-4 ${activePage === page.pageId ? "" : "hidden"}`} key={page.pageId}>
            <div className="space-y-4">
              {page.placeholders.map((placeholder) => {
                if (placeholder.type == DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES.IMAGE)
                  return (
                    <div key={placeholder.id}>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{placeholder.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Pole typu obrazek nie wymaga uzupełnienia w formularzu. Wygenerowany dokument będzie zawierał
                        obrazek przypisany do uczestnika (jeśli istnieje).
                      </p>
                    </div>
                  );
                if (placeholder.type == DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES.HTML)
                  return (
                    <PaziaFromQuill key={placeholder.id} label={placeholder.name} name={placeholder.id.toString()} />
                  );
                return <PaziaInput key={placeholder.id} label={placeholder.name} name={placeholder.id.toString()} />;
              })}
            </div>
          </div>
        );
      })}
    </Fragment>
  );
};

const FORM_NAMES = {
  TEMPLATEID: "templateId",
  NAME: "name",
  ACTIVE_PAGE: "activePage",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.TEMPLATEID]: yup.string().required(),
    [FORM_NAMES.NAME]: yup.string().required(),
    activePage: yup.mixed<string | number>().required(),
  })
  .required();

export type DocumentFormValues = yup.InferType<typeof schema>;

const PlaceholdersWarningPanel = ({ type }: { type?: { id: number; name: string } }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!type) return null;

  const placeholdeOptionsDescription = [
    {
      name: "Kurs",
      items: [
        { name: "grupa.nazwa", description: "Nazwa grupy kursów" },
        { name: "kurs.nazwa", description: "Nazwa kursu" },
        { name: "kurs.dataRozpoczecia", description: "Data rozpoczęcia kursu" },
        { name: "kurs.dataZakonczenia", description: "Data zakończenia kursu" },
      ],
    },
    {
      name: "Uczestnik",
      items: [
        { name: "student.nazwa", description: "Nazwa uczestnika (imie, nazwisko, tytuł)" },
        { name: "student.imie", description: "Imię uczestnika" },
        { name: "student.nazwisko", description: "Nazwisko uczestnika" },
        { name: "student.pesel", description: "Pesel uczestnika" },
        { name: "student.miejsceUrodzenia", description: "Miejsce urodzenia uczestnika" },
        { name: "student.dataUrodzenia", description: "Data urodzenia uczestnika" },
        { name: "student.zdjecieDokument", description: "Zdjęcie do dokumentów użytkownika" },
      ],
    },
    {
      name: "Dokument",
      items: [
        {
          name: `${type.name.toLowerCase()}.numer`,
          description: `Unikalny numer dla typu ${type.name}`,
        },
        {
          name: `${type.name.toLowerCase()}.dataWaznosci`,
          description: `Data ważności dla typu ${type.name}`,
        },
      ],
    },
  ];

  return (
    <>
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <svg
                className="h-4 w-4 text-amber-600 dark:text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">Dostępne zmienne dynamiczne</h3>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Te zmienne zostaną automatycznie zastąpione rzeczywistymi danymi podczas generowania dokumentów
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="rounded-md bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50"
          >
            Zobacz zmienne
          </button>
        </div>
      </div>

      <PaziaModal isOpen={isModalOpen} onCloseRequest={() => setIsModalOpen(false)} id="placeholders-modal" size="3xl">
        <div className="flex max-h-[80vh] flex-col">
          {/* Fixed header */}
          <div className="flex-shrink-0 border-b border-slate-200 p-6 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Dostępne zmienne dynamiczne</h3>
              <PaziaTooltip description={tooltip} />
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {placeholdeOptionsDescription.map((placeholderOption) => (
                <div key={placeholderOption.name}>
                  <h4 className="mb-3 font-medium text-slate-800 dark:text-slate-200">{placeholderOption.name}</h4>
                  <div className="space-y-2">
                    {placeholderOption.items.map((item) => (
                      <div
                        className="cursor-pointer rounded-lg border border-slate-200 p-3 transition-colors hover:bg-amber-50 dark:border-slate-700 dark:hover:bg-amber-900/10"
                        key={item.name}
                      >
                        <ClipboardText
                          text={"{{" + item.name + "}}"}
                          className="text-amber-800 dark:text-amber-200"
                          displayText={
                            <div className="flex items-center gap-3">
                              <code className="rounded bg-amber-100 px-2 py-1 font-mono text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                                {"{{" + item.name + "}}"}
                              </code>
                              <span className="text-sm text-slate-600 dark:text-slate-400">{item.description}</span>
                            </div>
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fixed footer */}
          <div className="flex-shrink-0 border-t border-slate-200 p-6 dark:border-slate-700">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      </PaziaModal>
    </>
  );
};

const tooltip =
  "Te zmienne zostaną automatycznie zastąpione rzeczywistymi danymi podczas generowania dokumentów dla uczestników";
